/**
 * PartnersPage - Liste des partners avec stats et actions admin
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Title, useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import StoreIcon from '@mui/icons-material/Store';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PendingIcon from '@mui/icons-material/Pending';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import ProvisionPartnerDialog from './ProvisionPartnerDialog';

// Queries & Mutations
import {
  GET_ADMIN_PARTNERS,
  GET_ADMIN_PARTNER_STATS,
  APPROVE_PARTNER,
  REJECT_PARTNER,
  SUSPEND_PARTNER,
  REACTIVATE_PARTNER,
} from './partners.queries';

import {
  PartnerWithStats,
  AdminPartnerStats,
  PartnerStatus,
  PartnerType,
  PartnerPlan,
  AdminPartnerFilterInput,
} from './partners.types';

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// Status colors
const STATUS_COLORS: Record<PartnerStatus, string> = {
  [PartnerStatus.PENDING]: '#F59E0B',
  [PartnerStatus.ACTIVE]: '#10B981',
  [PartnerStatus.SUSPENDED]: '#EF4444',
  [PartnerStatus.TERMINATED]: '#6B7280',
};

const TYPE_COLORS: Record<PartnerType, string> = {
  [PartnerType.INSTITUTIONAL]: '#3B82F6',
  [PartnerType.RETAIL]: '#8B5CF6',
  [PartnerType.FEDERATION]: '#EC4899',
};

// Stats Card Component
function StatsCard({
  title,
  value,
  icon,
  color,
  loading,
}: {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {loading ? <CircularProgress size={24} /> : value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PartnersPage() {
  const notify = useNotify();
  const navigate = useNavigate();

  // State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<PartnerType | ''>('');
  const [planFilter, setPlanFilter] = useState<PartnerPlan | ''>('');
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<PartnerWithStats | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const debouncedSearch = useDebounce(search, 500);

  // Build filter
  const filter: AdminPartnerFilterInput = {};
  if (debouncedSearch) filter.search = debouncedSearch;
  if (statusFilter) filter.status = statusFilter;
  if (typeFilter) filter.type = typeFilter;
  if (planFilter) filter.plan = planFilter;

  // Queries
  const {
    data: partnersData,
    loading: partnersLoading,
    refetch: refetchPartners,
  } = useQuery(GET_ADMIN_PARTNERS, {
    variables: { filter, page: page + 1, pageSize },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: statsData,
    loading: statsLoading,
    refetch: refetchStats,
  } = useQuery(GET_ADMIN_PARTNER_STATS, {
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [approvePartner] = useMutation(APPROVE_PARTNER, {
    onCompleted: () => {
      notify('Partner approuvé avec succès', { type: 'success' });
      refetchPartners();
      refetchStats();
    },
    onError: (error) => notify(`Erreur: ${error.message}`, { type: 'error' }),
  });

  const [rejectPartner] = useMutation(REJECT_PARTNER, {
    onCompleted: () => {
      notify('Partner rejeté', { type: 'warning' });
      refetchPartners();
      refetchStats();
    },
    onError: (error) => notify(`Erreur: ${error.message}`, { type: 'error' }),
  });

  const [suspendPartner] = useMutation(SUSPEND_PARTNER, {
    onCompleted: () => {
      notify('Partner suspendu', { type: 'warning' });
      refetchPartners();
      refetchStats();
    },
    onError: (error) => notify(`Erreur: ${error.message}`, { type: 'error' }),
  });

  const [reactivatePartner] = useMutation(REACTIVATE_PARTNER, {
    onCompleted: () => {
      notify('Partner réactivé avec succès', { type: 'success' });
      refetchPartners();
      refetchStats();
    },
    onError: (error) => notify(`Erreur: ${error.message}`, { type: 'error' }),
  });

  const partners = partnersData?.adminPartners?.items || [];
  const total = partnersData?.adminPartners?.total || 0;
  const stats: AdminPartnerStats = statsData?.adminPartnerStats || {};

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, partner: PartnerWithStats) => {
    setAnchorEl(event.currentTarget);
    setSelectedPartner(partner);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPartner(null);
  };

  const handleApprove = () => {
    if (selectedPartner) {
      approvePartner({ variables: { partnerId: selectedPartner.id } });
    }
    handleMenuClose();
  };

  const handleReject = () => {
    if (selectedPartner) {
      const reason = prompt('Raison du rejet:');
      if (reason) {
        rejectPartner({ variables: { partnerId: selectedPartner.id, reason } });
      }
    }
    handleMenuClose();
  };

  const handleSuspend = () => {
    if (selectedPartner) {
      const reason = prompt('Raison de la suspension:');
      if (reason) {
        suspendPartner({ variables: { partnerId: selectedPartner.id, reason } });
      }
    }
    handleMenuClose();
  };

  const handleReactivate = () => {
    if (selectedPartner) {
      reactivatePartner({ variables: { partnerId: selectedPartner.id } });
    }
    handleMenuClose();
  };

  const handleViewDetails = () => {
    if (selectedPartner) {
      navigate(`/partners/${selectedPartner.id}`);
    }
    handleMenuClose();
  };

  return (
    <Box>
      <Title title="Gestion Partners" />

      {/* Porte ++ (ADR 0022) — provisionnement admin d'un Partner structuré */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddBusinessIcon />}
          onClick={() => setProvisionOpen(true)}
        >
          Provisionner un partenaire
        </Button>
      </Box>
      <ProvisionPartnerDialog
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
        onSuccess={() => {
          refetchPartners();
          refetchStats();
        }}
      />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Partners"
            value={stats.totalPartners || 0}
            icon={<StoreIcon />}
            color="#3B82F6"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="En attente"
            value={stats.pendingApproval || 0}
            icon={<PendingIcon />}
            color="#F59E0B"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Clients"
            value={stats.totalEnrollments || 0}
            icon={<PeopleIcon />}
            color="#10B981"
            loading={statsLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Revenu Total"
            value={`${Math.round((stats.totalRevenue || 0) / 1000)}k FCFA`}
            icon={<AttachMoneyIcon />}
            color="#8B5CF6"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom ou slug..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as PartnerStatus | '')}
                label="Status"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={PartnerStatus.PENDING}>En attente</MenuItem>
                <MenuItem value={PartnerStatus.ACTIVE}>Actifs</MenuItem>
                <MenuItem value={PartnerStatus.SUSPENDED}>Suspendus</MenuItem>
                <MenuItem value={PartnerStatus.TERMINATED}>Résiliés</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as PartnerType | '')}
                label="Type"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={PartnerType.INSTITUTIONAL}>Institutionnel</MenuItem>
                <MenuItem value={PartnerType.RETAIL}>Retail</MenuItem>
                <MenuItem value={PartnerType.FEDERATION}>Fédération</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth>
              <InputLabel>Plan</InputLabel>
              <Select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value as PartnerPlan | '')}
                label="Plan"
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value={PartnerPlan.STARTER}>Starter</MenuItem>
                <MenuItem value={PartnerPlan.GROWTH}>Growth</MenuItem>
                <MenuItem value={PartnerPlan.PREMIUM}>Premium</MenuItem>
                <MenuItem value={PartnerPlan.ENTERPRISE}>Enterprise</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetchPartners()}
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Partner</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Clients</TableCell>
              <TableCell align="right">Transactions</TableCell>
              <TableCell align="right">Revenu</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partnersLoading && partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : partners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">Aucun partner trouvé</Typography>
                </TableCell>
              </TableRow>
            ) : (
              partners.map((partner: PartnerWithStats) => (
                <TableRow key={partner.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {partner.logoUrl && (
                        <Box
                          component="img"
                          src={partner.logoUrl}
                          alt={partner.name}
                          sx={{ width: 32, height: 32, borderRadius: 1 }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {partner.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {partner.slug}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={partner.type}
                      size="small"
                      sx={{ bgcolor: `${TYPE_COLORS[partner.type]}20`, color: TYPE_COLORS[partner.type] }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{partner.plan}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={partner.status}
                      size="small"
                      sx={{
                        bgcolor: `${STATUS_COLORS[partner.status]}20`,
                        color: STATUS_COLORS[partner.status],
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{partner.enrollmentsCount}</TableCell>
                  <TableCell align="right">{partner.transactionsCount}</TableCell>
                  <TableCell align="right">{Math.round(partner.totalRevenue / 1000)}k</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, partner)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Lignes par page"
        />
      </TableContainer>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <TrendingUpIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuItem>
        {selectedPartner?.status === PartnerStatus.PENDING && (
          <>
            <MenuItem onClick={handleApprove}>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Approuver</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleReject}>
              <ListItemIcon>
                <RemoveCircleIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Rejeter</ListItemText>
            </MenuItem>
          </>
        )}
        {selectedPartner?.status === PartnerStatus.ACTIVE && (
          <MenuItem onClick={handleSuspend}>
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Suspendre</ListItemText>
          </MenuItem>
        )}
        {selectedPartner?.status === PartnerStatus.SUSPENDED && (
          <MenuItem onClick={handleReactivate}>
            <ListItemIcon>
              <PlayCircleIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Réactiver</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
